import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
import { isBefore, parseISO } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { Redirect } from 'wouter';

import usePage from '../../hooks/usePage';
import { getComponentFromName } from '../../lib/utils';

import { useContest } from '../../contexts/ContestContext';
import DefaultComponent from './PlayDefault';
import * as Components from './play-index';

function PlayPage() {
    const route = useUrlGenerator();

    const { page, refetch: reload } = usePage();

    const {
        layout = 'default',
        games,
        participations,
        maxParticipations,
        canParticipateAt = null,
    } = page || {};

    const { type, theme, access = null } = useContest() || {};
    const { profileCompleted = true } = useUser();

    const { profile = null, withoutSingleGameRedirect = false } = access || {};
    const { required: profileRequired = false } = profile || {};
    const { name: themeName = null } = theme || {};

    const isIgaNoel = themeName === 'iga_noel';
    const isVoisin = themeName === 'voisin';
    const isNFL = type === 'nfl';
    const isLiveQuiz = type === 'live_quiz';
    const withoutRedirect =
        isIgaNoel || isVoisin || isNFL || isLiveQuiz || withoutSingleGameRedirect;

    const gamesCount = (games || []).length;

    const redirectToGame = useMemo(() => {
        if (withoutRedirect) {
            return null;
        }
        if (maxParticipations === 1) {
            const gamesParticipated = games.filter(
                ({ id }) =>
                    (participations.find(
                        ({ gameId, ended: participationEnded = false }) =>
                            gameId === id && !participationEnded,
                    ) || null) !== null,
            );
            if (gamesParticipated.length === maxParticipations) {
                return gamesParticipated[0];
            }
        }
        return gamesCount === 1 ? games[0] : null;
    }, [gamesCount, games, maxParticipations, participations, withoutRedirect]);

    const { url: redirectToGameUrl = null } = redirectToGame || {};

    const onTimerComplete = useCallback(() => {
        reload();
    }, [reload]);

    const canParticipateAtDate = useMemo(
        () => (canParticipateAt !== null ? parseISO(canParticipateAt) : null),
        [canParticipateAt],
    );

    const canPlay = useMemo(
        () => canParticipateAtDate === null || isBefore(canParticipateAtDate, new Date()),
        [canParticipateAtDate],
    );

    if (page === null) {
        return null;
    }

    if (!profileCompleted && profileRequired) {
        return (
            <Redirect
                to={`${route('register')}?next=${encodeURIComponent(
                    redirectToGameUrl || route('play'),
                )}`}
            />
        );
    }

    if (redirectToGameUrl !== null) {
        return <Redirect to={redirectToGameUrl} />;
    }

    const Component = getComponentFromName(Components, layout, DefaultComponent) || null;

    return Component !== null ? (
        <Component page={page} onTimerComplete={onTimerComplete} canPlay={canPlay} />
    ) : null;
}

export default PlayPage;
